import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import Main from 'components/layout/Main';
import { AppDispatch, CombinedStates } from 'core/types';
import { exportDailyAccessList, fetchDailyAccessList } from 'core/dailyReports/dailyReportsApi';
import { DalRow } from 'core/dailyReports/types';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import DailyReportDateBar, { DateRange } from 'pages/Dashboard/components/DailyReportDateBar';
import 'pages/Dashboard/components/dailyReportModal.scss';

// The builder joins the numbered visitor list with <br> for the HTML email;
// split it back into lines for safe rendering (no dangerouslySetInnerHTML).
const renderVisitors = (value: string) => {
    if (!value) return '-';
    return (
        <div>
            {value.split('<br>').map((line) => (
                <div key={line}>{line}</div>
            ))}
        </div>
    );
};

const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

// A range in the URL as from/to; absent means the single `date` day.
const rangeFromParams = (from: string | null, to: string | null): DateRange | null => (
    from && to ? [dayjs(from).startOf('day'), dayjs(to).startOf('day')] : null
);

const DailyAccessList: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const { dalRows, isFetchingDal, isExporting, error } = useSelector((state: CombinedStates) => state.dailyReports);

    const dateParam = searchParams.get('date');
    const [date, setDate] = useState<Dayjs>(dateParam ? dayjs(dateParam).startOf('day') : dayjs().startOf('day'));
    const [range, setRange] = useState<DateRange | null>(
        rangeFromParams(searchParams.get('from'), searchParams.get('to')),
    );

    // A range supersedes the single date; without one, the range is that one day.
    const from = range ? range[0] : date;
    const to = range ? range[1] : date;

    const refresh = useCallback((): void => {
        dispatch(fetchDailyAccessList({
            date: (range ? range[0] : date).format('YYYY-MM-DD'),
            endDate: range ? range[1].format('YYYY-MM-DD') : undefined,
        }));
    }, [dispatch, date, range]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Keep the dates in the URL so the Back link from a request returns here on
    // the same day (or range).
    const syncUrl = (value: Dayjs, nextRange: DateRange | null): void => {
        setSearchParams(
            nextRange
                ? {
                    date: value.format('YYYY-MM-DD'),
                    from: nextRange[0].format('YYYY-MM-DD'),
                    to: nextRange[1].format('YYYY-MM-DD'),
                }
                : { date: value.format('YYYY-MM-DD') },
            { replace: true },
        );
    };

    const handleDateChange = (value: Dayjs) => {
        setDate(value);
        syncUrl(value, range);
    };

    const handleRangeChange = (value: DateRange | null): void => {
        setRange(value);
        syncUrl(date, value);
    };

    const openRequest = (row: DalRow) => {
        if (!row.encryptedId) {
            return;
        }
        const params = range
            ? `date=${date.format('YYYY-MM-DD')}&from=${from.format('YYYY-MM-DD')}&to=${to.format('YYYY-MM-DD')}`
            : `date=${date.format('YYYY-MM-DD')}`;
        navigate(`/visitor-view-request/${row.encryptedId}`, { state: { returnTo: `/daily-access-list?${params}` } });
    };

    const columns: ColumnsType<DalRow> = [
        {
            title: 'ID',
            dataIndex: 'requestId',
            key: 'requestId',
            width: 70,
            render: (value: number, row: DalRow) => (
                row.encryptedId
                    ? <Button type="link" size="small" className="daily-report-modal__id-link" onClick={() => openRequest(row)}>{value}</Button>
                    : value
            ),
        },
        { title: 'Site', dataIndex: 'siteName', key: 'siteName' },
        // Which day the row falls on. Only worth a column when the list spans
        // more than one day; otherwise the date bar already says.
        ...(range ? [{
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (value: string | null) => (value ? dayjs(value).format(DISPLAY_DATE_FORMAT) : '-'),
        }] : []),
        { title: 'From', dataIndex: 'startTime', key: 'startTime' },
        { title: 'To', dataIndex: 'endTime', key: 'endTime' },
        { title: 'Requester', dataIndex: 'requesterName', key: 'requesterName' },
        { title: 'Visitor Name', dataIndex: 'visitorName', key: 'visitorName', render: renderVisitors },
    ];

    const toolbar = buildMainToolbar(userData?.role?.accessLevel);

    return (
        <Main
            className="login"
            title={<div>Daily Access List</div>}
            subHeaderTitle="Daily Access List"
            subHeaderHasBackLink
            subHeaderBackLinkCallback={() => navigate('/dashboard')}
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            floatingHeader={false}
            footerContent=""
            floatingFooter={false}
            showFooter={false}>
            <div className="main-container dashboard">
                <div className="daily-report-page">
                    <DailyReportDateBar
                        date={date}
                        onChange={handleDateChange}
                        showDateArrows
                        range={range}
                        onRangeChange={handleRangeChange}
                        onExport={() => dispatch(exportDailyAccessList({
                            date: from.format('YYYY-MM-DD'),
                            endDate: range ? to.format('YYYY-MM-DD') : undefined,
                        }))}
                        isExporting={isExporting}
                        exportDisabled={dalRows.length === 0} />

                    {error && !isFetchingDal ? (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Couldn't load the daily access list. Please try again or pick another date." />
                    ) : null}

                    <Table<DalRow>
                        rowKey={(row, index) => `${row.requestId}-${index}`}
                        columns={columns}
                        dataSource={dalRows}
                        loading={isFetchingDal}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                        locale={{ emptyText: 'No access list entries for this date.' }} />
                </div>
            </div>
        </Main>
    );
};

export default DailyAccessList;
